import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./Charts.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";

const COLORS = ["#a92c2c", "#f97316", "#facc15", "#4ade80"] as const;

type Severity = "Critical" | "High" | "Medium" | "Low";
type PercentagesMap = Record<string, number>;
type TopRow = { name: string; count: number };
const ORDER: Severity[] = ["Critical", "High", "Medium", "Low"];

type VersionMetricKey =
  | "count"
  | "avg_total"
  | "avg_critical"
  | "avg_high"
  | "avg_medium"
  | "avg_low";

type VersionPoint = {
  version_index: number;
} & Record<VersionMetricKey, number>;

const METRIC_OPTIONS: { key: VersionMetricKey; label: string }[] = [
  { key: "avg_total", label: "Avg total CVEs" },
  { key: "avg_critical", label: "Avg critical CVEs" },
  { key: "avg_high", label: "Avg high CVEs" },
  { key: "avg_medium", label: "Avg medium CVEs" },
  { key: "avg_low", label: "Avg low CVEs" },
];

const metricLabel = (k: VersionMetricKey) =>
  METRIC_OPTIONS.find((m) => m.key === k)?.label ?? k;

type Props = {
  // Optional: if not provided, we’ll fetch the precomputed cached percentages ourselves.
  percentages?: PercentagesMap | null;
};

export default function ChartsSection({ percentages }: Props) {
  // ---------- LINE CHART STATE ----------
  const [verLine, setVerLine] = useState<VersionPoint[]>([]);
  const [selectedMetric, setSelectedMetric] =
    useState<VersionMetricKey>("avg_total");
  const [loadingLine, setLoadingLine] = useState(true);
  const [lineErr, setLineErr] = useState<string | null>(null);

  // ---------- PIE CHART STATE ----------
  const [internalPercentages, setInternalPercentages] =
    useState<PercentagesMap | null>(null);
  const [pieData, setPieData] = useState<{ name: Severity; value: number }[]>(
    []
  );
  const [pieErr, setPieErr] = useState<string | null>(null);
  const [pieLoading, setPieLoading] = useState(false);

  // ---------- POPOVER STATE ----------
  const [popOpen, setPopOpen] = useState(false);
  const [popSeverity, setPopSeverity] = useState<Severity | "">("");
  const [popRows, setPopRows] = useState<TopRow[]>([]);
  const [popLoading, setPopLoading] = useState(false);
  const pieWrapRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const hasExternalPercentages = typeof percentages !== "undefined";
  const effectivePercentages = hasExternalPercentages
    ? percentages
    : internalPercentages;

  // ---------- LINE CHART (imagesPerVersion; precomputed in /statistics table) ----------
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Uses precomputed /statistics/images_per_version
        const raw = await api.stats.imagesPerVersion();
        if (ignore) return;

        const normalized: VersionPoint[] = (raw || []).map((row: any) => ({
          version_index: Number(
            row.version_index ?? row.idx ?? row.version_order ?? 0
          ),
          count: Number(row.count ?? row.layer_count ?? 0),

          avg_total: Number(row.avg_total ?? row.total_total ?? 0),
          avg_critical: Number(row.avg_critical ?? row.critical_total ?? 0),
          avg_high: Number(row.avg_high ?? row.high_total ?? 0),
          avg_medium: Number(row.avg_medium ?? row.medium_total ?? 0),
          avg_low: Number(row.avg_low ?? row.low_total ?? 0),
        }));

        setVerLine(normalized);
        setLineErr(null);
      } catch (e: any) {
        if (!ignore) setLineErr(e?.message ?? "Failed to load");
      } finally {
        if (!ignore) setLoadingLine(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // ---------- FETCH PRECOMPUTED PERCENTAGES IF NOT PROVIDED ----------
  useEffect(() => {
    if (hasExternalPercentages) {
      // Parent is giving us the (already precomputed) data.
      return;
    }

    let ignore = false;
    (async () => {
      try {
        setPieLoading(true);
        setPieErr(null);
        // Hits /statistics/cached/percentages, which uses the precomputed cache.
        const raw = await api.stats.percentage();
        if (ignore) return;
        setInternalPercentages(raw || {});
      } catch (e: any) {
        if (!ignore) setPieErr(e?.message ?? "Failed to load severity data");
      } finally {
        if (!ignore) setPieLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [hasExternalPercentages]);

  // ---------- BUILD PIE DATA FROM EFFECTIVE PERCENTAGES ----------
  useEffect(() => {
    if (!effectivePercentages) {
      setPieData([]);
      if (!hasExternalPercentages && !pieLoading && !pieErr) {
        // nothing yet, keep "Loading…" placeholder
      }
      return;
    }
    try {
      const formattedPie = ORDER.map((k) => ({
        name: k,
        value: Number((effectivePercentages as PercentagesMap)[k] ?? 0),
      }));
      setPieData(formattedPie);
      setPieErr(null);
    } catch (e: any) {
      setPieErr(e?.message ?? "Failed to build severity chart");
      setPieData([]);
    }
  }, [effectivePercentages, hasExternalPercentages, pieLoading]);

  // ---------- POPOVER (Top repos by severity; still API-based, but small) ----------
  const openSeverityPopover = async (severity: Severity) => {
    setPopOpen(true);
    setPopSeverity(severity);
    setPopLoading(true);
    setPopRows([]);
    try {
      const raw = await api.stats.topReposBySeverity(severity);
      const rows: TopRow[] = (raw || []).map((r: any) => ({
        name: r.repo ?? r.version ?? r.name ?? "",
        count: Number(r.count ?? 0),
      }));
      setPopRows(rows.slice(0, 5));
    } finally {
      setPopLoading(false);
    }
  };

  const closePopover = () => {
    setPopOpen(false);
    setPopSeverity("");
    setPopRows([]);
  };

  useEffect(() => {
    if (!popOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopover();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popOpen]);

  useEffect(() => {
    if (!popOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (pieWrapRef.current?.contains(t)) return;
      closePopover();
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [popOpen]);

  const hasMetricData =
    verLine.length > 0 &&
    verLine.some((p) => Number(p[selectedMetric]) > 0);

  return (
    <div className="viz-grid">
      {/* LINE CHART */}
      <section className="viz-card viz-card--tall">
        <h4 className="viz-card-title">
          Images &amp; Average Vulnerabilities per Version Index
        </h4>

        <div className="viz-metric-toggle">
          {METRIC_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={
                "viz-metric-pill" +
                (selectedMetric === opt.key ? " viz-metric-pill--active" : "")
              }
              onClick={() => setSelectedMetric(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loadingLine ? (
          <div className="viz-empty">Loading…</div>
        ) : verLine.length === 0 ? (
          <div className="viz-empty">
            {lineErr ? `Error: ${lineErr}` : "No data"}
          </div>
        ) : !hasMetricData ? (
          <div className="viz-empty">
            No data available for {metricLabel(selectedMetric)}.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={420}>
            <LineChart
              data={verLine}
              margin={{ top: 10, right: 28, left: 12, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="version_index"
                allowDecimals={false}
                tick={{ fontSize: 12 }}
                domain={[1, "dataMax"]}
                label={{
                  value: "version_index",
                  position: "insideBottom",
                  dy: 24,
                }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                label={{
                  value: metricLabel(selectedMetric),
                  angle: -90,
                  position: "insideLeft",
                  dx: -8,
                }}
              />
              <Tooltip
                formatter={(v: number) => Number(v).toFixed(2)}
                labelFormatter={(l: any) => `version_index ${l}`}
              />
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke="#1f3a93"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
                name={metricLabel(selectedMetric)}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* PIE CHART */}
      <section className="viz-card viz-card--tall" ref={pieWrapRef}>
        <h4 className="viz-card-title">
          Overall Severity Distribution (All Repositories)
        </h4>
        {pieData.length === 0 ? (
          <div className="viz-empty">
            {pieErr
              ? `Error: ${pieErr}`
              : pieLoading
              ? "Loading…"
              : "No data"}
          </div>
        ) : (
          <ResponsiveContainer width="95%" height={420}>
            <PieChart margin={{ top: 6, right: 60, left: 60, bottom: 40 }}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%"
                innerRadius="30%"
                outerRadius="70%"
                paddingAngle={3}
                labelLine
                label={({ name, value }) =>
                  `${name}: ${Math.round(Number(value))}%`
                }
              >
                {pieData.map((slice, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={COLORS[idx % COLORS.length]}
                    className="pie-slice"
                    onClick={(e) => {
                      e.stopPropagation();
                      openSeverityPopover(slice.name as Severity);
                    }}
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                wrapperStyle={{ fontSize: 13.5 }}
              />
              <Tooltip formatter={(v: number) => `${Number(v).toFixed(1)}%`} />
            </PieChart>
          </ResponsiveContainer>
        )}

        {popOpen && (
          <div
            className="sev-popover"
            ref={popRef}
            role="dialog"
            aria-modal="false"
          >
            <div className="sev-popover__header">
              <h4 className="sev-popover__title">
                Top 5 {popSeverity} versions
              </h4>
              <button
                className="sev-popover__close"
                onClick={closePopover}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {popLoading ? (
              <div className="sev-popover__empty">Loading…</div>
            ) : !popRows.length ? (
              <div className="sev-popover__empty">No data</div>
            ) : (
              <ul className="sev-popover__list">
                {popRows.map((r, i) => {
                  const target = `/versions/${encodeURIComponent(
                    r.name.replace(/\//g, "_")
                  )}`;
                  return (
                    <li key={`${r.name}-${i}`} className="sev-popover__item">
                      <a
                        className="sev-popover__name"
                        href={target}
                        title={r.name}
                      >
                        {r.name}
                      </a>
                      <span className="sev-popover__count">{r.count}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="sev-popover__hint">
              Click a row to open details
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
